import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Calendar, ChevronDown, Search, Plus } from 'lucide-react';
import ALL_ROLES from '../data/experience.json';
import useMediaQuery, { PHONE } from '../hooks/useMediaQuery';

const EASE = [0.16, 1, 0.3, 1];

/* ─── Curated highlights ──────────────────────────────────────────────── */
/* The strongest roles, written up in full. Everything else lives in the    */
/* searchable archive below.                                                */

const PRIMARY_EXPERIENCES = [
  {
    title: 'Founder & CEO',
    company: 'Govtelligence',
    location: 'Leicester · Hybrid',
    period: 'Apr 2026 – Present',
    description:
      'Founded an AI governance and compliance company helping organisations implement responsible, regulation-aligned AI systems and decision frameworks. Lead strategy, operations and product across governance advisory, compliance intelligence and enterprise AI risk, building frameworks for the EU AI Act and model-risk obligations.',
    tags: ['AI Governance', 'EU AI Act', 'Founding', 'Go-to-Market', 'Model Risk'],
    featured: true,
    accent: '#D5B98C',
    url: 'https://www.govtelligence.com',
  },
  {
    title: 'Independent Expert Consultant',
    company: 'AlphaSights · Tegus · Guidepoint · NewtonX',
    location: 'Remote',
    period: 'Mar 2025 – Present',
    description:
      'Vetted independently across four of the world\'s leading expert networks. Delivered 1,000+ engagements advising institutional investors, strategy teams, and C-suite clients on AI commercialisation, ML deployment, and data strategy. AlphaSights clients expect precise, commercially-grounded analysis with no tolerance for vague answers. The breadth across four independently-screened platforms is verifiable.',
    tags: ['Expert Networks', 'AI Strategy', 'Data Science', '1,000+ Engagements', 'Institutional Clients'],
    featured: true,
    accent: '#00E5FF',
  },
  {
    title: 'AI Adoption Strategist',
    company: 'Prolific',
    location: 'Remote',
    period: 'Apr 2026 – Present',
    description:
      'Develop and deliver strategic recommendations on AI tool adoption, identifying friction points and proposing workflow integration solutions. Assess AI readiness across business functions with structured reports that guide data-driven adoption decisions, and annotate AI outputs to support model evaluation.',
    tags: ['AI Adoption', 'AI Readiness', 'Workflow Integration', 'Model Evaluation'],
    accent: '#9B3FFE',
  },
  {
    title: 'Consulting Project Expert & Expert Panel',
    company: 'Practera · Robin & Berry',
    location: 'Remote',
    period: 'Jul 2026 – Present',
    description:
      'Collaborate on consulting projects that analyse business and technology challenges and develop evidence-based recommendations. Structured research, analysis and stakeholder-oriented communication inside multidisciplinary teams, applying data, AI and strategy skills to produce decision-ready outputs.',
    tags: ['Consulting', 'Research', 'Stakeholder Communication', 'AI & Strategy'],
    accent: '#10B981',
  },
  {
    title: 'Founder & CEO',
    company: 'Etokify AI Agency',
    location: 'Leicester / Remote',
    period: 'Apr 2025 – Present',
    description:
      'Built and launched an AI automation agency from zero to live client engagements. Delivered a churn prediction model to a gym operator and lead generation systems for B2B clients. Led a distributed team, managed delivery across concurrent clients, and owned every client relationship from pitch to completion.',
    tags: ['Founding', 'AI Automation', 'B2B', 'Team Leadership', 'Client Delivery'],
    accent: '#F59E0B',
  },
  {
    title: 'Forward Program in Leadership & Consulting',
    company: 'McKinsey & Company',
    location: 'Remote',
    period: 'Apr 2026 – Present',
    description:
      'Selected for McKinsey\'s competitive Forward Programme for high-potential talent from underrepresented backgrounds. Structured consulting training in problem structuring, hypothesis-driven thinking and client communication, with case-based modules simulating real strategy engagements. Certificate and badge issued July 2026.',
    tags: ['Consulting', 'Problem Structuring', 'Leadership', 'Competitive Selection'],
    accent: '#3B82F6',
  },
];

const ADDITIONAL_EXPERIENCES = [
  {
    title: 'Business Analyst',
    company: 'Alignerr',
    location: 'Remote',
    period: 'Mar 2026 – Present',
    description:
      'Assessed LLM outputs for reasoning quality and commercial accuracy across a range of industries. Used business analysis frameworks to evaluate model performance against real-world use cases. Identified failure patterns that standard benchmarks tend to miss and fed findings back into the evaluation pipeline.',
    tags: ['AI Evaluation', 'Business Analysis', 'LLM Quality', 'Model Alignment'],
  },
  {
    title: 'AI Trainer & Prompt Engineer',
    company: 'Outlier & DataAnnotation',
    location: 'Remote',
    period: 'Jan 2026 – Present',
    description:
      'Evaluated LLM outputs in reasoning, coding, and STEM for accuracy and consistency. Designed prompt engineering protocols and evaluation criteria aimed at production quality rather than benchmark performance. Identified reasoning gaps and escalated patterns to improve training pipelines.',
    tags: ['LLM Evaluation', 'Prompt Engineering', 'RLHF', 'AI Training'],
  },
  {
    title: 'Public Sector AI Consultant',
    company: 'UK Digital Networks',
    location: 'Leicester',
    period: 'Nov 2025 – Present',
    description:
      'Advised on AI procurement for a public sector organisation covering approximately 1,000 schools and NHS Trusts. Mapped AI solutions to compliance requirements and institutional constraints. Helped develop AI procurement strategies within tightly regulated, policy-driven environments.',
    tags: ['Public Sector', 'AI Procurement', 'Policy', 'NHS'],
  },
  {
    title: 'Freelance AI & Data Consultant',
    company: 'Upwork',
    location: 'Remote',
    period: 'Dec 2025 – Apr 2026',
    description:
      'Delivered data strategy and AI consulting to clients across industries on a freelance basis. Managed multiple concurrent projects and maintained a five-star public rating. Work quality is verified by client reviews, not self-reported.',
    tags: ['Data Strategy', 'AI Consulting', '5★ Rating', 'Client Delivery'],
  },
  {
    title: 'AI Talent Member',
    company: 'Turing',
    location: 'Remote',
    period: 'Mar 2026 – Apr 2026',
    description:
      'Accepted into the Turing AI Talent Pool after clearing a multi-stage technical screening covering skills, experience, and career trajectory. A vetted network of AI and data science professionals with access to senior roles at leading tech and research organisations. Still active in the community.',
    tags: ['AI Talent Network', 'Competitive Selection', 'Data Science', 'Machine Learning'],
  },
  {
    title: 'AI Lead Generation & Sales Consultant',
    company: 'Hokstad Consulting',
    location: 'Leicester',
    period: 'Aug 2025 – Present',
    description:
      'Developed B2B outreach strategy and tailored AI audit propositions for prospective clients. Achieved a 30% increase in client acquisition through targeted outreach and personalised consultations.',
    tags: ['AI Strategy', 'B2B Sales', 'Client Acquisition', 'Lead Generation'],
  },
];

const ARCHIVE_TABS = [
  { id: 'all', label: 'All' },
  { id: 'Current roles', label: 'Current' },
  { id: 'Programmes & networks', label: 'Programmes' },
  { id: 'Job simulations', label: 'Simulations' },
  { id: 'Previous roles', label: 'Previous' },
];

const ARCHIVE_PREVIEW = 10;

const currentCount = ALL_ROLES.filter((r) => /Present/.test(r.period)).length;
const foundedCount = ALL_ROLES.filter((r) => /Founder/.test(r.title)).length;

/* ─── Highlight card ──────────────────────────────────────────────────── */
const ExperienceCard = ({ exp, index }) => {
  const accent = exp.accent || '#9B3FFE';
  return (
    <motion.article
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.65, delay: (index % 2) * 0.08, ease: EASE }}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        e.currentTarget.style.setProperty('--sx', `${e.clientX - r.left}px`);
        e.currentTarget.style.setProperty('--sy', `${e.clientY - r.top}px`);
      }}
      className={`exp-card relative group p-7 md:p-8 rounded-[22px] border overflow-hidden ${
        exp.featured ? 'exp-card-featured' : ''
      }`}
      style={{ '--accent': accent }}
      data-testid={`experience-item-${index}`}
    >
      <span
        className="absolute inset-x-8 top-0 h-px opacity-60 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
      />

      <div className="relative flex flex-wrap items-start justify-between gap-3 mb-5">
        <span
          className={`inline-flex items-center gap-2 px-3 py-1 text-xs font-medium rounded-full ${
            exp.featured ? 'bg-white text-black' : 'bg-white/[0.07] text-white/85 border border-white/[0.09]'
          }`}
        >
          {!exp.featured && <span className="h-1.5 w-1.5 rounded-full" style={{ background: accent }} />}
          {exp.company}
        </span>

        <div className="flex flex-wrap items-center gap-3.5 text-[10.5px] text-text-secondary">
          <span className="flex items-center gap-1.5">
            <MapPin className="w-3 h-3" />
            {exp.location}
          </span>
          <span className="flex items-center gap-1.5">
            <Calendar className="w-3 h-3" />
            {exp.period}
          </span>
        </div>
      </div>

      <h3 className="relative font-display text-xl md:text-[1.4rem] font-medium tracking-tight mb-3 leading-tight">
        {exp.title}
        {/Present/.test(exp.period) && (
          <span className="ml-2.5 inline-flex translate-y-[-3px] items-center gap-1.5 rounded-full border border-emerald-400/20 bg-emerald-400/[0.08] px-2 py-0.5 align-middle text-[9px] font-medium uppercase tracking-[0.18em] text-emerald-300">
            <span className="h-1 w-1 rounded-full bg-emerald-400" />
            Active
          </span>
        )}
      </h3>

      <p className="relative text-text-secondary text-sm leading-[1.74] mb-5">{exp.description}</p>

      <div className="relative flex flex-wrap items-center gap-2">
        {exp.tags.map((tag) => (
          <span
            key={tag}
            className="px-2.5 py-1 text-[11px] rounded-full text-text-secondary border border-white/[0.06] bg-white/[0.035] transition-colors duration-300 group-hover:text-white/70"
          >
            {tag}
          </span>
        ))}
        {exp.url && (
          <a
            href={exp.url}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto py-2.5 text-[11.5px] font-medium text-white/70 hover:text-white underline-animation"
          >
            Visit site ↗
          </a>
        )}
      </div>
    </motion.article>
  );
};

/* ─── Archive row ─────────────────────────────────────────────────────── */
const ArchiveRow = ({ role, open, onToggle }) => {
  const active = /Present/.test(role.period);
  return (
    <motion.li
      layout="position"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="relative border-b border-white/[0.06] last:border-b-0"
    >
      <button
        onClick={onToggle}
        aria-expanded={open}
        className="w-full grid grid-cols-[1fr_auto] md:grid-cols-[170px_1fr_auto] items-center gap-x-6 gap-y-1 px-5 md:px-7 py-4 text-left hover:bg-white/[0.025] transition-colors duration-200"
      >
        <span className="hidden md:flex items-center gap-2.5 text-[11.5px] text-text-secondary tabular-nums">
          <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${active ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]' : 'bg-white/20'}`} />
          {role.period}
        </span>
        <span className="min-w-0">
          <span className="block text-[14.5px] font-medium text-white/90 leading-snug">{role.title}</span>
          <span className="block text-[12.5px] text-text-secondary mt-0.5">
            {role.company}
            <span className="md:hidden"> · {role.period}</span>
          </span>
        </span>
        <span className={`flex h-7 w-7 items-center justify-center rounded-full border border-white/10 transition-all duration-300 ${open ? 'rotate-45 bg-white text-black' : 'text-white/60'}`}>
          <Plus className="h-3.5 w-3.5" />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="overflow-hidden"
          >
            <div className="px-5 md:px-7 pb-5 md:pl-[calc(170px+3.25rem)]">
              <p className="text-[13.5px] leading-[1.75] text-text-secondary max-w-[75ch]">{role.description}</p>
              {role.location && (
                <p className="mt-2.5 flex items-center gap-1.5 text-[11px] text-white/35">
                  <MapPin className="h-3 w-3" /> {role.location}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.li>
  );
};

/* ─── Section ─────────────────────────────────────────────────────────── */
const Experience = () => {
  const [showAll, setShowAll] = useState(false);
  const [tab, setTab] = useState('all');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState(null);
  const [archiveExpanded, setArchiveExpanded] = useState(false);
  const isPhone = useMediaQuery(PHONE);
  const archivePreview = isPhone ? 6 : ARCHIVE_PREVIEW;
  const primaryCount = isPhone ? 4 : PRIMARY_EXPERIENCES.length;
  const highlights = showAll
    ? [...PRIMARY_EXPERIENCES.slice(primaryCount), ...ADDITIONAL_EXPERIENCES]
    : [];

  const archive = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ALL_ROLES.filter(
      (r) =>
        (tab === 'all' || r.category === tab) &&
        (!q || `${r.title} ${r.company} ${r.description}`.toLowerCase().includes(q))
    );
  }, [tab, query]);

  const visibleArchive = archiveExpanded || query ? archive : archive.slice(0, archivePreview);
  const tabCount = (id) => (id === 'all' ? ALL_ROLES.length : ALL_ROLES.filter((r) => r.category === id).length);

  return (
    <section id="experience" className="relative py-20 sm:py-28 md:py-40" data-testid="experience-section">
      <div className="absolute top-0 left-0 right-0 h-px section-divider" />
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 50% 35% at 85% 10%, rgba(0,229,255,0.05), transparent 70%)' }} />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.75, ease: EASE }}
          className="mb-14 md:mb-16"
        >
          <div className="section-label mb-6">Experience</div>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none font-medium">
              Professional
              <br />
              <span className="gradient-text-brand">Track Record</span>
            </h2>
            <div className="grid grid-cols-3 rounded-2xl border border-white/[0.07] bg-white/[0.02] backdrop-blur-md overflow-hidden lg:min-w-[440px]">
              {[
                { n: ALL_ROLES.length, l: 'Roles & programmes' },
                { n: currentCount, l: 'Active now' },
                { n: foundedCount, l: 'Ventures founded' },
              ].map((s, i) => (
                <div key={s.l} className={`px-5 py-4 ${i < 2 ? 'border-r border-white/[0.07]' : ''}`}>
                  <p className="font-display text-3xl font-medium leading-none tabular-nums">{s.n}</p>
                  <p className="mt-2 text-[9.5px] uppercase tracking-[0.2em] text-text-secondary">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Highlights */}
        <div className="grid md:grid-cols-2 gap-4 md:gap-5">
          {PRIMARY_EXPERIENCES.slice(0, primaryCount).map((exp, index) => (
            <ExperienceCard key={exp.company + exp.title} exp={exp} index={index} />
          ))}
        </div>

        <AnimatePresence>
          {showAll && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="overflow-hidden"
            >
              <div className="grid md:grid-cols-2 gap-4 md:gap-5 pt-4 md:pt-5">
                {highlights.map((exp, index) => (
                  <ExperienceCard key={exp.company + exp.title} exp={exp} index={index} />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex justify-center mt-10">
          <motion.button
            onClick={() => setShowAll((v) => !v)}
            className="group flex items-center gap-2 px-7 py-3.5 border border-white/[0.12] rounded-full text-sm font-medium text-text-secondary hover:text-white hover:bg-white/[0.04] hover:border-white/[0.28] transition-all duration-300"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
          >
            {showAll ? 'Show fewer highlights' : `Show ${PRIMARY_EXPERIENCES.length - primaryCount + ADDITIONAL_EXPERIENCES.length} more highlights`}
            <motion.span animate={{ rotate: showAll ? 180 : 0 }} transition={{ duration: 0.3 }} className="flex">
              <ChevronDown className="w-4 h-4" />
            </motion.span>
          </motion.button>
        </div>

        {/* Full archive */}
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.75, ease: EASE }}
          className="mt-24"
        >
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-6">
            <div>
              <p className="section-label mb-4">Full career archive</p>
              <h3 className="font-display text-3xl md:text-4xl font-medium tracking-tight">Every role, searchable.</h3>
            </div>
            <label className="relative block w-full md:w-[320px]">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/35 pointer-events-none" />
              <input
                type="search"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setOpenId(null); }}
                placeholder="Search roles, companies, skills…"
                aria-label="Search experience"
                className="w-full rounded-full border border-white/[0.1] bg-white/[0.03] py-3 pl-11 pr-4 text-sm text-white placeholder:text-white/30 focus:border-white/30 focus:bg-white/[0.05] transition-colors"
              />
            </label>
          </div>

          <div className="mb-4 -mx-6 px-6 overflow-x-auto no-scrollbar">
            <div className="inline-flex gap-1 p-1 rounded-full border border-white/[0.08] bg-white/[0.025]">
              {ARCHIVE_TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => { setTab(t.id); setOpenId(null); setArchiveExpanded(false); }}
                  aria-pressed={tab === t.id}
                  className={`relative px-4 py-2 rounded-full text-[12.5px] font-medium whitespace-nowrap transition-colors duration-300 ${
                    tab === t.id ? 'text-black' : 'text-text-secondary hover:text-white'
                  }`}
                >
                  {tab === t.id && (
                    <motion.span layoutId="exp-tab" className="absolute inset-0 rounded-full bg-white" transition={{ type: 'spring', stiffness: 380, damping: 32 }} />
                  )}
                  <span className="relative z-10 flex items-center gap-1.5">
                    {t.label}
                    <span className={`text-[10px] tabular-nums ${tab === t.id ? 'text-black/50' : 'text-white/30'}`}>{tabCount(t.id)}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-[22px] border border-white/[0.07] bg-[rgba(12,12,16,0.7)] backdrop-blur-md overflow-hidden">
            {visibleArchive.length ? (
              <ul>
                <AnimatePresence initial={false}>
                  {visibleArchive.map((role) => (
                    <ArchiveRow
                      key={role.id}
                      role={role}
                      open={openId === role.id}
                      onToggle={() => setOpenId((id) => (id === role.id ? null : role.id))}
                    />
                  ))}
                </AnimatePresence>
              </ul>
            ) : (
              <p className="px-7 py-10 text-center text-sm text-text-secondary">No roles match “{query}”.</p>
            )}
          </div>

          {!query && archive.length > archivePreview && (
            <div className="flex justify-center mt-6">
              <button
                onClick={() => setArchiveExpanded((v) => !v)}
                className="flex items-center gap-2 px-5 py-3 text-sm font-medium text-text-secondary hover:text-white transition-colors"
              >
                {archiveExpanded ? 'Collapse archive' : `Show all ${archive.length}`}
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${archiveExpanded ? 'rotate-180' : ''}`} />
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;
